import { Dispatch, SetStateAction, useCallback, useContext, useMemo, useRef } from 'react';
import { LandingContext, ERequestStages, ITariffContextState } from '~/src/containers/ContextContainer';
import { getCitiesTariffs, ICitiesTariffs } from '~/src/services/api/api-content/cities/tariffs';
import { singletonHook } from '~/src/containers/SingletonHook';

type TSetTariff = Dispatch<SetStateAction<ITariffContextState>>;

/**
 * **ВАЖНО**: Это абстрактный хук.
 * Не экспортировать и не вызывать его напрямую!
 */
const useGetTariffsRequest = (providerId: number, stage: ERequestStages, setTariffs?: TSetTariff) => {
    const requestSended = useRef(0);

    const getTariffsData = useCallback(async () => {
        setTariffs((prevState) => ({ ...prevState, stage: ERequestStages.requestSended }));
        const response = await getCitiesTariffs(providerId, { availableNew: 1, landingAvailable: 1 });

        if (response.isSuccess) {
            const { mono, bundles } = response.payload;
            setTariffs({ mono, bundles, stage: ERequestStages.loaded });
            return;
        }
        setTariffs({ mono: [], bundles: [], stage: ERequestStages.error });
    }, [providerId, setTariffs]);

    const getTariffs = useCallback(() => {
        if (requestSended.current === 0 && stage === ERequestStages.init || stage === ERequestStages.error) {
            requestSended.current += 1;
            getTariffsData();
        }
    }, [getTariffsData, stage, requestSended]);

    return { getTariffs };
};

const useGetTariffsSingleton = singletonHook<({ getTariffs: () => void, }), [number, ERequestStages, TSetTariff]>(
    {
        getTariffs: () => ({}),
    },
    useGetTariffsRequest);

export const useGetTariffs = <T extends unknown = ICitiesTariffs>(tariffType?: 'mono' | 'bundle'): [
    boolean,
    T,
    () => void,
] => {
    const { city, tariffs } = useContext(LandingContext);
    const [tariffsInfo, setTariffsInfo] = tariffs;
    const { stage, ...tariffsInfoByTypes } = tariffsInfo;
    const { getTariffs } = useGetTariffsSingleton([city?.provider_id || 47, stage, setTariffsInfo]);

    const isLoading = useMemo(() => stage === ERequestStages.loading, [stage]);
    const filteredTariffs = useMemo(() =>
            tariffType ? tariffsInfoByTypes[tariffType] : ({ ...tariffsInfoByTypes }),
        [tariffType, tariffsInfoByTypes]);

    return [isLoading, filteredTariffs, getTariffs];
};
